import { useState, useEffect, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Zap,
  Loader2,
  RefreshCw,
  Calendar,
  Target,
  Eye,
  BarChart3,
  PieChart,
  Clock,
  CheckCircle,
  XCircle,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Users,
  Globe,
  MessageSquare,
  Share2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrendsAnalytics() {
  const { darkMode, region, brand } = useOutletContext() || { darkMode: true, region: 'UAE', brand: 'blackswan' };
  const [trends, setTrends] = useState([]);
  const [shareOfVoice, setShareOfVoice] = useState(null);
  const [winLossAnalysis, setWinLossAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detectingTrends, setDetectingTrends] = useState(false);
  const [activeTab, setActiveTab] = useState("trends");

  const fetchData = useCallback(async () => {
    try {
      const [trendsRes, sovRes, wlRes] = await Promise.all([
        axios.get(`${API}/advanced/trends/${brand}/${region}`),
        axios.get(`${API}/advanced/share-of-voice/${brand}/${region}`),
        axios.get(`${API}/advanced/win-loss/analysis/${brand}/${region}`)
      ]);
      setTrends(trendsRes.data.trends || []);
      setShareOfVoice(sovRes.data);
      setWinLossAnalysis(wlRes.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  }, [brand, region]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Memoized win-loss aggregates to avoid re-computing on every render
  const winLossSummary = useMemo(() => {
    const byComp = Object.values(winLossAnalysis?.by_competitor || {});
    const totalWins = byComp.reduce((sum, c) => sum + (c.wins || 0), 0);
    const totalLosses = byComp.reduce((sum, c) => sum + (c.losses || 0), 0);
    const avgWinRate = byComp.length
      ? Math.round(byComp.reduce((sum, c) => sum + (c.win_rate || 0), 0) / byComp.length)
      : 0;
    return { totalWins, totalLosses, avgWinRate };
  }, [winLossAnalysis]);

  const detectTrends = async () => {
    setDetectingTrends(true);
    try {
      const res = await axios.post(`${API}/advanced/trends/detect/${brand}/${region}`);
      toast.success(`Detected ${res.data.detected || 0} new trends!`);
      fetchData();
    } catch (error) {
      toast.error("Failed to detect trends");
    } finally {
      setDetectingTrends(false);
    }
  };

  const getImpactColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-amber-500 text-white';
      case 'medium': return 'bg-blue-500 text-white';
      case 'low': return 'bg-gray-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getTrendIcon = (type) => {
    switch (type) {
      case 'emerging': return <TrendingUp className="w-5 h-5 text-emerald-500" />;
      case 'declining': return <TrendingDown className="w-5 h-5 text-red-500" />;
      case 'disruptive': return <Zap className="w-5 h-5 text-purple-500" />;
      default: return <Minus className="w-5 h-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#DC143C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Market Trends & Analytics
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            AI-powered market intelligence and trend detection
          </p>
        </div>
        <Button
          onClick={detectTrends}
          disabled={detectingTrends}
          className="bg-[#DC143C] hover:bg-[#b91031]"
        >
          {detectingTrends ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Zap className="w-4 h-4 mr-2" />
          )}
          Detect New Trends
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={darkMode ? 'bg-[#161b22]' : ''}>
          <TabsTrigger value="trends">Market Trends</TabsTrigger>
          <TabsTrigger value="sov">Share of Voice</TabsTrigger>
          <TabsTrigger value="winloss">Win/Loss Analysis</TabsTrigger>
        </TabsList>

        {/* Market Trends Tab */}
        <TabsContent value="trends" className="space-y-4 mt-4">
          {trends.length > 0 ? (
            <div className="grid gap-4">
              {trends.map((trend, idx) => (
                <Card key={trend.id || idx} className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl ${
                        trend.trend_type === 'emerging' ? 'bg-emerald-500/10' :
                        trend.trend_type === 'declining' ? 'bg-red-500/10' :
                        trend.trend_type === 'disruptive' ? 'bg-purple-500/10' :
                        'bg-gray-500/10'
                      }`}>
                        {getTrendIcon(trend.trend_type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {trend.title}
                          </h3>
                          <Badge className={getImpactColor(trend.impact_level)}>
                            {trend.impact_level}
                          </Badge>
                          <Badge className="bg-purple-500/10 text-purple-500 border-0">
                            {trend.category}
                          </Badge>
                        </div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {trend.description}
                        </p>
                        
                        {trend.recommended_actions?.length > 0 && (
                          <div className="mt-3">
                            <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              Recommended Actions:
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {trend.recommended_actions.map((action) => (
                                <Badge key={action} className="bg-blue-500/10 text-blue-500 border-0 text-xs">
                                  {action}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-4 mt-3 text-xs">
                          <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
                            Confidence: {Math.round((trend.confidence || 0) * 100)}%
                          </span>
                          <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
                            Urgency: {trend.urgency}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardContent className="py-12 text-center">
                <TrendingUp className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  No Trends Detected Yet
                </h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Click "Detect New Trends" to analyze market data
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Share of Voice Tab */}
        <TabsContent value="sov" className="space-y-4 mt-4">
          <div className="grid grid-cols-3 gap-4">
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-[#DC143C]/10">
                    <MessageSquare className="w-5 h-5 text-[#DC143C]" />
                  </div>
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Mentions</p>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {shareOfVoice?.total_mentions || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-emerald-500/10">
                    <Users className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Competitors Tracked</p>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {Object.keys(shareOfVoice?.competitors || {}).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-purple-500/10">
                    <Globe className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Platforms</p>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>6</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
            <CardHeader>
              <CardTitle className={darkMode ? 'text-white' : ''}>Competitor Share of Voice</CardTitle>
              <CardDescription>Ranked by total mentions and social presence</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(shareOfVoice?.competitors || {}).slice(0, 10).map(([id, data], idx) => (
                  <div key={id} className="flex items-center gap-4">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx === 0 ? 'bg-[#DC143C] text-white' :
                      idx === 1 ? 'bg-amber-500 text-white' :
                      idx === 2 ? 'bg-emerald-500 text-white' :
                      darkMode ? 'bg-[#21262d] text-gray-400' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {data.name}
                        </span>
                        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {data.mentions} mentions ({data.percentage}%)
                        </span>
                      </div>
                      <div className={`h-2 rounded-full ${darkMode ? 'bg-[#21262d]' : 'bg-gray-100'}`}>
                        <div 
                          className="h-2 rounded-full bg-[#DC143C]"
                          style={{ width: `${Math.min(data.percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Win/Loss Analysis Tab */}
        <TabsContent value="winloss" className="space-y-4 mt-4">
          <div className="grid grid-cols-4 gap-4">
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardContent className="p-5 text-center">
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Records</p>
                <p className={`text-3xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {winLossAnalysis?.total_records || 0}
                </p>
              </CardContent>
            </Card>
            
            <Card className={`${darkMode ? 'bg-[#161b22] border-[#30363d]' : ''} border-l-4 border-l-emerald-500`}>
              <CardContent className="p-5 text-center">
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Wins</p>
                <p className="text-3xl font-bold mt-1 text-emerald-500">
                  {winLossSummary.totalWins}
                </p>
              </CardContent>
            </Card>
            
            <Card className={`${darkMode ? 'bg-[#161b22] border-[#30363d]' : ''} border-l-4 border-l-red-500`}>
              <CardContent className="p-5 text-center">
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Losses</p>
                <p className="text-3xl font-bold mt-1 text-red-500">
                  {winLossSummary.totalLosses}
                </p>
              </CardContent>
            </Card>
            
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardContent className="p-5 text-center">
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Win Rate</p>
                <p className={`text-3xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {winLossAnalysis?.total_records > 0 ? `${winLossSummary.avgWinRate}%` : '0%'}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardHeader>
                <CardTitle className={`text-lg ${darkMode ? 'text-white' : ''}`}>
                  <CheckCircle className="w-5 h-5 inline mr-2 text-emerald-500" />
                  Top Win Reasons
                </CardTitle>
              </CardHeader>
              <CardContent>
                {winLossAnalysis?.top_win_reasons?.length > 0 ? (
                  <div className="space-y-3">
                    {winLossAnalysis.top_win_reasons.map((item) => (
                      <div key={item.reason} className="flex items-center justify-between">
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {item.reason}
                        </span>
                        <Badge className="bg-emerald-500/10 text-emerald-500 border-0">
                          {item.count}x
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    No win records yet. Start recording wins to see patterns.
                  </p>
                )}
              </CardContent>
            </Card>
            
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardHeader>
                <CardTitle className={`text-lg ${darkMode ? 'text-white' : ''}`}>
                  <XCircle className="w-5 h-5 inline mr-2 text-red-500" />
                  Top Loss Reasons
                </CardTitle>
              </CardHeader>
              <CardContent>
                {winLossAnalysis?.top_loss_reasons?.length > 0 ? (
                  <div className="space-y-3">
                    {winLossAnalysis.top_loss_reasons.map((item) => (
                      <div key={item.reason} className="flex items-center justify-between">
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {item.reason}
                        </span>
                        <Badge className="bg-red-500/10 text-red-500 border-0">
                          {item.count}x
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    No loss records yet. Analyze your losses to improve.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
